/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1673568000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Jan 13 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "servicenow",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Maximum exection time exceeded",
          "depth": 1,
          "id": "maximum-exection-time-exceeded"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      img: "img",
      h3: "h3"
    }, _provideComponents(), props.components), {LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "The ServiceNow Platform is a scalable cloud service that offers a range of applications, workflows and processes. Sharing common architecture and data, the platform incorporates a variety of ready-made suites catering for specific business areas."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Standard Objects"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to records in ServiceNow"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, check out the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ServiceNow and then enter the name of your ServiceNow instance and credentials for an integration user."]
      }), "\n", _jsx(_components.p, {
        children: "Your integration user should have the following roles:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "rest_service"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "web_service_admin"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "rest_api_explorer"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["Read access to the ", _jsx(_components.code, {
            children: "sys_db_object"
          }), " table"]
        }), "\n", _jsxs(_components.li, {
          children: ["Read access to the ", _jsx(_components.code, {
            children: "sys_glide_object"
          }), " table"]
        }), "\n", _jsxs(_components.li, {
          children: ["Read access to the ", _jsx(_components.code, {
            children: "sys_dictionary"
          }), " table"]
        }), "\n", _jsx(_components.li, {
          children: "Create/Write/Read access to the table being synced to"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-servicenow-setup.png",
          alt: "Integration role selection in the ServiceNow UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your ServiceNow destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the ServiceNow destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "You can match records from your source to your ServiceNow instance using any unique field in ServiceNow. This can be a custom field or an out-of-box field, as long as it's guaranteed to be unique. If the selected field isn't unique, the incorrect record may be updated."
      }), "\n", _jsxs(_components.p, {
        children: ["If you're using the ", _jsx(_components.code, {
          children: "sys_id"
        }), " field, the value from the source must be a 32-character, lowercase, alphanumeric string."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-servicenow-idfieldmapping.png",
          alt: "Record matching in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can also sync columns from your source to any of the fields in the selected ServiceNow table."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-servicenow-fieldmappings.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "maximum-exection-time-exceeded",
        children: "Maximum exection time exceeded"
      }), "\n", _jsxs(_components.p, {
        children: ["ServiceNow has some default request limits that can be configured in the ", _jsx(_components.code, {
          children: "Transaction Quota Rules (sysrule_quota)"
        }), " table.\nIf you're seeing ", _jsx(_components.code, {
          children: "Transaction cancelled: maximum execution time exceeded"
        }), " try decreasing the batch size in your sync configuration, or increasing the timeout duration of ", _jsx(_components.code, {
          children: "REST Batch API request timeout"
        }), " and ", _jsx(_components.code, {
          children: "REST Table API request timeout"
        }), " in the Transaction Quota Rules table."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
